import React, {useEffect, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {Button, Segmented, Spin, Upload} from "antd";
import styles from '../../styles/editAnnoucement.module.scss';
import {toast} from "react-toastify";
import dayjs from 'dayjs';
import {
    FloatingLabelDatePicker,
    FloatingLabelInput,
    FloatingLabelSelect,
    FloatingLabelTextArea
} from "../../components/elements/floatingLabelInput/FloatingLabelInput";
import CustomSelect from "../../components/elements/select/Select";
import TranslateButton from "../../components/elements/TranslateButton/TranslateButton";
import { useTranslateFields } from "../../hooks/useTranslateFields";
import announcementService from "../../api/announcement-api";
import {
    ANNOUNCEMENT_STATUS_OPTIONS,
    APP_OPEN_PAGE_OPTION,
    DISPLAY_PAGE_OPTIONS,
    DISPLAY_FREQUENCY_OPTIONS,
    EVERY_TIME_FREQUENCY,
    MEDIA_TYPE_OPTIONS,
    TARGET_USER_GROUP_OPTIONS,
    validatedForm,
    syncDisplayInPageWithFrequency,
} from "../../utils/announcement.util";

export default function EditAnnouncement () {
    const location = useLocation();
    const navigate = useNavigate();
    const { id } = useParams();

    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const [mode, setMode] = useState('edit');

    useEffect(() => {
        initializeComponent();
    }, [id, location.state]);

    const initializeComponent = async () => {
        // Case 1: Passed from form (create mode)
        if (location.state?.formData && location.state?.mode === 'create') {
            console.log('Create mode with data:', location.state.formData);
            setFormData(location.state.formData);
            setMode('create');
            return;
        }

        // Case 2: ID in URL - fetch from API (edit mode)
        if (id) {
            await fetchAnnouncementById(id);
            setMode('edit');
            return;
        }

        // Case 3: No data - redirect back
        toast.error('Nema podataka za prikaz');
        navigate('/objave');
    };

    const fetchAnnouncementById = async (announcementId) => {
        setLoading(true);
        try {
            const response = await announcementService.getById(announcementId);
            console.log('Fetched announcement:', response.data);
            setFormData(response.data);
        } catch (error) {
            console.error('Error fetching announcement:', error);
            toast.error('Greška pri učitavanju objave');
            navigate('/objave');
        } finally {
            setLoading(false);
        }
    };

    // Convert file to base64 data URL
    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    };

    // Handle image upload
    const handleImageUpload = async (file) => {
        // Validate file type
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            toast.error('Dozvoljen je samo JPG, PNG ili WebP format');
            return false;
        }

        // Validate file size (5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxSize) {
            toast.error('Slika je prevelika. Maksimalna veličina je 5MB');
            return false;
        }

        setUploadingImage(true);
        try {
            // Convert to base64
            const base64 = await fileToBase64(file);

            // Update form data with base64 (will be uploaded to S3 on submit)
            updateFormData('imageUrl', base64);

            toast.success('Slika je uspešno učitana');
        } catch (error) {
            console.error('Error uploading image:', error);
            toast.error('Greška pri učitavanju slike');
        } finally {
            setUploadingImage(false);
        }

        return false; // Prevent default upload behavior
    };

    const handleSubmit = async () => {
        if (!validatedForm(formData)){
            return;
        }
        setLoading(true);
        try {
            if (mode === 'create') {
                const response = await announcementService.create(formData);
                if (!response.success) {
                    toast.error(response.message || 'Greška prilikom čuvanja objave');
                    return;
                }
                toast.success('Objava je uspešno kreirana!');
                navigate('/objave');
            } else if (mode === 'edit') {
                const response = await announcementService.update(formData._id, formData);
                if (!response.success) {
                    toast.error(response.message || 'Greška prilikom čuvanja objave');
                    return;
                }
                toast.success('Objava je uspešno ažurirana!');
                navigate('/objave');
            }
        } catch (error) {
            console.error('Error submitting:', error);
            toast.error(error.response?.data?.message || 'Greška prilikom čuvanja objave');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        if (mode === 'create') {
            navigate('/objave', {
                state: { formData, openFormTab: true }
            });
        } else {
            navigate('/objave');
        }
    };

    const handleDeleteImage = () => {
        updateFormData('imageUrl', '');
    };

    // Handle video upload
    const handleVideoUpload = async (file) => {
        // Validate file type
        if (file.type !== 'video/mp4') {
            toast.error('Dozvoljen je samo MP4 format');
            return false;
        }

        // Validate file size (50MB)
        const maxSize = 50 * 1024 * 1024; // 50MB in bytes
        if (file.size > maxSize) {
            toast.error('Video je prevelik. Maksimalna veličina je 50MB');
            return false;
        }

        setUploadingVideo(true);
        try {
            // Convert to base64
            const base64 = await fileToBase64(file);

            // Update form data with base64 (will be uploaded to S3 on submit)
            updateFormData('videoUrl', base64);

            toast.success('Video je uspešno učitan');
        } catch (error) {
            console.error('Error uploading video:', error);
            toast.error('Greška pri učitavanju videa');
        } finally {
            setUploadingVideo(false);
        }

        return false; // Prevent default upload behavior
    };

    const handleDeleteVideo = () => {
        updateFormData('videoUrl', '');
    };

    const handleMediaTypeChange = (value) => {
        updateFormData('mediaType', value);
        if (value === 'video') {
            updateFormData('imageUrl', undefined);
        } else {
            updateFormData('videoUrl', undefined);
        }
    };

    const updateFormData = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const { translating, translate } = useTranslateFields();

    const handleTranslate = async () => {
        if (!formData.displayName?.sr?.trim()) {
            toast.error('Popunite naslov na srpskom pre prevođenja');
            return;
        }

        const fields = { displayName: formData.displayName.sr };
        if (formData.displayDescription?.sr?.trim()) {
            fields.displayDescription = formData.displayDescription.sr;
        }
        if (formData.displayText?.sr?.trim()) {
            fields.displayText = formData.displayText.sr;
        }

        const result = await translate(fields);
        if (!result) return;

        // Merge hu+en into a single updateFormData call per field — see the same
        // note in AddAnnoucementStep1.js: updateFormData closes over `formData`
        // directly, so two back-to-back calls for the same field would clobber.
        updateFormData('displayName', {
            ...formData.displayName,
            hu: result.hu.displayName,
            en: result.en.displayName,
        });
        if (result.hu.displayDescription !== undefined || result.en.displayDescription !== undefined) {
            updateFormData('displayDescription', {
                ...formData.displayDescription,
                ...(result.hu.displayDescription !== undefined ? { hu: result.hu.displayDescription } : {}),
                ...(result.en.displayDescription !== undefined ? { en: result.en.displayDescription } : {}),
            });
        }
        if (result.hu.displayText !== undefined || result.en.displayText !== undefined) {
            updateFormData('displayText', {
                ...formData.displayText,
                ...(result.hu.displayText !== undefined ? { hu: result.hu.displayText.slice(0, 500) } : {}),
                ...(result.en.displayText !== undefined ? { en: result.en.displayText.slice(0, 500) } : {}),
            });
        }

        toast.success('Prevod je uspešno primenjen');
    };

    useEffect(() => {
        if (!formData) return;
        syncDisplayInPageWithFrequency(formData, updateFormData);
    }, [formData?.displayFrequency]);

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <Spin size="large" />
            </div>
        );
    }

    if (!formData) {
        return null;
    }

    return (
        <div className={styles.editAnnouncementWrapper}>
            {/* Header with Back */}
            <div className={styles.topBar}>
                <div className="link_btn" onClick={handleBack} style={{ cursor: 'pointer' }}>
                    <img alt="back" src="/assets/icons/chevron.svg" />
                    <span>Nazad</span>
                </div>
            </div>

            {/* Main Content Card */}
            <div className={styles.contentCard}>
                {/* Osnovne informacije */}
                <section className={styles.section}>
                    <h3 className={styles.sectionTitle}>Osnovne informacije</h3>
                    <div className={styles.formRowDouble}>
                        <FloatingLabelInput
                            label="Interni naziv"
                            value={formData.name}
                            onChange={(e) => updateFormData('name', e.target.value)}
                            placeholder="Objava za januar"
                        />
                        <CustomSelect
                            value={ANNOUNCEMENT_STATUS_OPTIONS.find(opt => opt.value === formData.status)}
                            onChange={(value) => updateFormData('status', value)}
                            items={ANNOUNCEMENT_STATUS_OPTIONS}
                            placeholder="Izaberi status"
                        />
                    </div>
                </section>

                {/* Sadržaj objave */}
                <section className={styles.section}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 className={styles.sectionTitle}>Sadržaj objave</h3>
                        <TranslateButton onClick={handleTranslate} loading={translating} />
                    </div>
                    <div className={styles.formGrid}>
                        <div className={styles.formGroupLeft}>
                            <FloatingLabelInput
                                label="Naslov"
                                value={formData.displayName?.sr}
                                onChange={(e) => updateFormData('displayName', { ...formData.displayName, sr: e.target.value })}
                                placeholder="Tvoja dnevna doza zdravlja"
                            />
                            <FloatingLabelInput
                                label="Kratak opis (opciono)"
                                value={formData.displayDescription?.sr}
                                onChange={(e) => updateFormData('displayDescription', { ...formData.displayDescription, sr: e.target.value })}
                            />
                        </div>
                        <div className={styles.formGroupRight}>
                            <FloatingLabelTextArea
                                label="Tekst objave (maks. 500 karaktera)"
                                value={formData.displayText?.sr}
                                onChange={(e) => updateFormData('displayText', { ...formData.displayText, sr: e.target.value })}
                                maxLength={500}
                                rows={6}
                            />
                        </div>
                    </div>
                </section>

                {/* Mađarski prevod */}
                <section className={styles.section}>
                    <h3 className={styles.sectionTitle}>Sadržaj objave - mađarski prevod</h3>
                    <div className={styles.formGrid}>
                        <div className={styles.formGroupLeft}>
                            <FloatingLabelInput
                                label="Naslov"
                                value={formData.displayName?.hu}
                                onChange={(e) => updateFormData('displayName', { ...formData.displayName, hu: e.target.value })}
                                placeholder="Tvoja dnevna doza zdravlja"
                            />
                            <FloatingLabelInput
                                label="Kratak opis (opciono)"
                                value={formData.displayDescription?.hu}
                                onChange={(e) => updateFormData('displayDescription', { ...formData.displayDescription, hu: e.target.value })}
                            />
                        </div>
                        <div className={styles.formGroupRight}>
                            <FloatingLabelTextArea
                                label="Tekst objave (maks. 500 karaktera)"
                                value={formData.displayText?.hu}
                                onChange={(e) => updateFormData('displayText', { ...formData.displayText, hu: e.target.value })}
                                maxLength={500}
                                rows={6}
                            />
                        </div>
                    </div>
                </section>

                {/* Engleski prevod */}
                <section className={styles.section}>
                    <h3 className={styles.sectionTitle}>Sadržaj objave - engleski prevod</h3>
                    <div className={styles.formGrid}>
                        <div className={styles.formGroupLeft}>
                            <FloatingLabelInput
                                label="Naslov"
                                value={formData.displayName?.en}
                                onChange={(e) => updateFormData('displayName', { ...formData.displayName, en: e.target.value })}
                                placeholder="Tvoja dnevna doza zdravlja"
                            />
                            <FloatingLabelInput
                                label="Kratak opis (opciono)"
                                value={formData.displayDescription?.en}
                                onChange={(e) => updateFormData('displayDescription', { ...formData.displayDescription, en: e.target.value })}
                            />
                        </div>
                        <div className={styles.formGroupRight}>
                            <FloatingLabelTextArea
                                label="Tekst objave (maks. 500 karaktera)"
                                value={formData.displayText?.en}
                                onChange={(e) => updateFormData('displayText', { ...formData.displayText, en: e.target.value })}
                                maxLength={500}
                                rows={6}
                            />
                        </div>
                    </div>
                </section>

                {/* Podešavanje linka */}
                <section className={styles.section}>
                    <h3 className={styles.sectionTitle}>Podešavanje linka</h3>
                    <div className={styles.formRowTriple}>
                        <FloatingLabelInput
                            label="Link"
                            value={formData.link}
                            onChange={(e) => updateFormData('link', e.target.value)}
                            placeholder="https://eliksirvitalnosti.com/"
                        />
                    </div>
                </section>

                {/* Priloži fotografiju ili video */}
                <section className={styles.section}>
                    <h3 className={styles.sectionTitle}>Sadržaj (slika ili video)</h3>
                    <Segmented
                        block
                        value={formData.mediaType || 'image'}
                        onChange={handleMediaTypeChange}
                        options={MEDIA_TYPE_OPTIONS}
                        style={{ marginBottom: 16 }}
                    />
                    {formData.mediaType === 'video' ? (
                        <div className={styles.photoUploadContainer}>
                            <div className={styles.previewWrapper}>
                                {formData.videoUrl ? (
                                    <>
                                        <video
                                            src={formData.videoUrl}
                                            controls
                                            className={styles.imagePreview}
                                        />
                                        <button
                                            className={styles.deleteBtn}
                                            onClick={handleDeleteVideo}
                                            type="button"
                                            disabled={uploadingVideo}
                                        >
                                            <span className={styles.trashIcon}>🗑️</span>
                                        </button>
                                    </>
                                ) : (
                                    <img
                                        src="/logo.svg"
                                        alt="Placeholder"
                                        className={styles.imagePreview}
                                    />
                                )}
                            </div>
                            <Upload
                                accept="video/mp4"
                                beforeUpload={handleVideoUpload}
                                showUploadList={false}
                                disabled={uploadingVideo}
                                className={styles.uploadBoxWrapper}
                            >
                                <div className={styles.uploadBox}>
                                    <div className={styles.uploadContent}>
                                        <div className={styles.cloudIconContainer}>
                                            {uploadingVideo ? (
                                                <Spin />
                                            ) : (
                                                <span className={styles.cloudIcon}>☁️</span>
                                            )}
                                        </div>
                                        <p className={styles.uploadText}>
                                            <span className={styles.highlight}>
                                                {uploadingVideo ? 'Učitavanje...' : 'Kliknite za otpremanje videa'}
                                            </span> ili prevuci i ispusti
                                        </p>
                                        <small className={styles.fileTypes}>.MP4 (max. 50MB)</small>
                                    </div>
                                </div>
                            </Upload>
                        </div>
                    ) : (
                        <div className={styles.photoUploadContainer}>
                            <div className={styles.previewWrapper}>
                                {formData.imageUrl ? (
                                    <>
                                        <img
                                            src={formData.imageUrl}
                                            alt="Preview"
                                            className={styles.imagePreview}
                                        />
                                        <button
                                            className={styles.deleteBtn}
                                            onClick={handleDeleteImage}
                                            type="button"
                                            disabled={uploadingImage}
                                        >
                                            <span className={styles.trashIcon}>🗑️</span>
                                        </button>
                                    </>
                                ) : (
                                    <img
                                        src="/logo.svg"
                                        alt="Placeholder"
                                        className={styles.imagePreview}
                                    />
                                )}
                            </div>
                            <Upload
                                accept="image/jpeg,image/jpg,image/png,image/webp"
                                beforeUpload={handleImageUpload}
                                showUploadList={false}
                                disabled={uploadingImage}
                                className={styles.uploadBoxWrapper}
                            >
                                <div className={styles.uploadBox}>
                                    <div className={styles.uploadContent}>
                                        <div className={styles.cloudIconContainer}>
                                            {uploadingImage ? (
                                                <Spin />
                                            ) : (
                                                <span className={styles.cloudIcon}>☁️</span>
                                            )}
                                        </div>
                                        <p className={styles.uploadText}>
                                            <span className={styles.highlight}>
                                                {uploadingImage ? 'Učitavanje...' : 'Kliknite za otpremanje slike'}
                                            </span> ili prevuci i ispusti
                                        </p>
                                        <small className={styles.fileTypes}>.JPG, .PNG, .WebP (max. 5MB)</small>
                                    </div>
                                </div>
                            </Upload>
                        </div>
                    )}
                </section>

                {/* Prikaz u aplikaciji */}
                <section className={styles.section}>
                    <h3 className={styles.sectionTitle}>Prikaz u aplikaciji</h3>
                    <div className={styles.formRowDouble}>
                        <FloatingLabelSelect
                            label="Izbor stranice u aplikaciji"
                            value={formData.displayInPage}
                            onChange={(value) => updateFormData('displayInPage', value)}
                            items={formData.displayFrequency === EVERY_TIME_FREQUENCY ? [APP_OPEN_PAGE_OPTION] : DISPLAY_PAGE_OPTIONS}
                            disabled={formData.displayFrequency === EVERY_TIME_FREQUENCY}
                            placeholder="Izaberi stranicu"
                        />
                    </div>
                </section>

                {/* Učestalost prikazivanja */}
                <section className={styles.section}>
                    <h3 className={styles.sectionTitle}>Učestalost prikazivanja</h3>
                    <div className={styles.formRowTriple}>
                        <FloatingLabelDatePicker
                            label="Start date"
                            value={formData.startDate ? dayjs(formData.startDate) : null}
                            onChange={(date) => updateFormData('startDate', date ? date.toISOString() : null)}
                            format="DD.MM.YYYY."
                            placeholder="29.01.2026."
                        />
                        <FloatingLabelDatePicker
                            label="End date"
                            value={formData.endDate ? dayjs(formData.endDate) : null}
                            onChange={(date) => updateFormData('endDate', date ? date.toISOString() : null)}
                            format="DD.MM.YYYY."
                            placeholder="29.01.2026."
                        />
                        <FloatingLabelSelect
                            label="Frekvencija prikaza"
                            value={formData.displayFrequency}
                            onChange={(value) => updateFormData('displayFrequency', value)}
                            items={DISPLAY_FREQUENCY_OPTIONS}
                            placeholder="Izaberi frekvenciju"
                        />
                    </div>
                </section>

                {/* Targeting korisnika */}
                <section className={styles.section}>
                    <h3 className={styles.sectionTitle}>Targeting korisnika</h3>
                    <div className={styles.formRowTriple}>
                        <FloatingLabelSelect
                            label="Izaberi grupu korisnika"
                            value={formData.targetUserGroup}
                            onChange={(value) => updateFormData('targetUserGroup', value)}
                            items={TARGET_USER_GROUP_OPTIONS}
                            placeholder="Izaberi grupu"
                        />
                    </div>
                </section>
            </div>

            {/* Bottom Action Button */}
            <div className={styles.bottomAction}>
                <Button
                    className="btn-primary"
                    onClick={handleSubmit}
                    loading={loading}
                    disabled={uploadingImage}
                >
                    {mode === 'edit' ? 'Sačuvaj izmene' : 'Kreiraj objavu'}
                </Button>
            </div>
        </div>
    );
};